// @givmo/app-admin — Settings → Account info tab (07-1.4).
//
// "Account information" with an Edit → Save toggle + Cancel (explicit, dirty-gated, edit-mode).
// State off CompanyDetailedOut. Save via PUT /admin/company (CompanyUpdate — all fields optional):
//   - Company name (text)
//   - Location (autocomplete) — edit-mode shows a debounced search → GET /admin/company/location;
//     each prediction's ", USA" suffix is stripped (Flutter parity); there is NO location-save
//     endpoint, so the chosen `description` STRING is persisted via the CompanyUpdate PUT.
//   - # of Employees (integer)
//   - Company logo — current `logo_url` shown; UPLOAD via POST /admin/company/profile_picture
//     (multipart, the generated `companiesUploadCompanyProfilePicture` — the company's logo IS its
//     profile picture). After a successful upload a `?v=` cache-buster forces a fresh fetch.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  companiesGetCompanyQueryKey,
  companiesGetUserLocationOptions,
  companiesUpdateCompanyAccountInfoMutation,
  companiesUploadCompanyProfilePictureMutation,
} from '@givmo/api-client/query';
import {
  isGivmoError,
  supportReference,
  type CompanyDetailedOut,
  type CompanyUpdate,
  type LocationPredictionOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Field,
  Icon,
  UploadDropzone,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { Pencil } from 'lucide-react';

const PREFIX = 'settings-account';
const LOCATION_DEBOUNCE_MS = 300;

// ── Company-logo upload pre-check (SEC-BE10) ───────────────────────────────────────────────────────
// The backend `POST /admin/company/profile_picture` is authoritative and validates by FILE EXTENSION
// (`ALLOWED_LOGO_EXTENSIONS = [.png, .jpg, .jpeg, .webp]`) + a 10 MB cap (`MAX_LOGO_SIZE`). These
// constants mirror it exactly so a wrong file is caught client-side with a clear message before it
// ever leaves the browser — never a substitute for the server check, just a fail-fast for the user.

/** The `accept` attribute for the logo file input (`image/jpeg` covers both `.jpg` and `.jpeg`). */
export const ACCEPTED_LOGO_TYPES = 'image/png,image/jpeg,image/webp';

/** Backend-accepted logo extensions, lowercased (exact mirror of `ALLOWED_LOGO_EXTENSIONS`). */
const ALLOWED_LOGO_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp'] as const;

/** Backend logo size cap: 10 MB (exact mirror of `MAX_LOGO_SIZE`). */
const MAX_LOGO_BYTES = 10 * 1024 * 1024;

/**
 * Client-side pre-check for a picked logo file, mirroring the backend guards exactly. Returns a clear
 * error message to surface (and skip the upload), or `null` when the file is acceptable.
 */
export function validateLogoFile(file: File): string | null {
  const name = file.name.toLowerCase();
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot) : '';
  if (!(ALLOWED_LOGO_EXTENSIONS as readonly string[]).includes(ext)) {
    return 'Choose a PNG, JPG, JPEG, or WebP image.';
  }
  if (file.size > MAX_LOGO_BYTES) {
    return 'That image is larger than 10 MB. Choose a smaller file.';
  }
  return null;
}

/** Strip a trailing ", USA" from a location prediction's description (Flutter parity). */
export function stripUsaSuffix(description: string): string {
  return description.replace(/,\s*USA$/i, '');
}

interface AccountForm {
  name: string;
  location: string;
  employees: string; // kept as a string for the input; parsed to an integer on save.
}

function readAccountForm(company: CompanyDetailedOut): AccountForm {
  return {
    name: company.name ?? '',
    location: company.location ?? '',
    employees:
      typeof company.number_of_employees === 'number' ? String(company.number_of_employees) : '',
  };
}

/** Build the CompanyUpdate PUT body from the form, sending only changed fields. */
export function buildAccountUpdate(company: CompanyDetailedOut, form: AccountForm): CompanyUpdate {
  const body: CompanyUpdate = {};
  const savedName = company.name ?? '';
  const savedLocation = company.location ?? '';
  const savedEmployees =
    typeof company.number_of_employees === 'number' ? company.number_of_employees : null;

  if (form.name !== savedName) body.name = form.name;
  if (form.location !== savedLocation) body.location = form.location;

  const parsedEmployees = parseEmployees(form.employees);
  if (parsedEmployees !== savedEmployees && parsedEmployees !== null) {
    body.number_of_employees = parsedEmployees;
  }
  return body;
}

/** Parse the employees input to a non-negative integer, or `null` for empty/invalid. */
export function parseEmployees(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n >= 0 ? n : null;
}

export interface SettingsAccountTabProps {
  company: CompanyDetailedOut;
}

export function SettingsAccountTab({ company }: SettingsAccountTabProps) {
  const queryClient = useQueryClient();
  const saved = useMemo(() => readAccountForm(company), [company]);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<AccountForm>(saved);
  const [error, setError] = useState<string | null>(null);
  const [errorReference, setErrorReference] = useState<string | null>(null);

  // Re-prime when the saved company changes, unless mid-edit (don't clobber in-progress edits).
  useEffect(() => {
    if (!editing) setForm(saved);
  }, [saved, editing]);

  const dirty =
    form.name !== saved.name ||
    form.location !== saved.location ||
    form.employees !== saved.employees;

  const updateMutation = useMutation({
    ...companiesUpdateCompanyAccountInfoMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: companiesGetCompanyQueryKey() });
      toast.success('Account information saved');
      setEditing(false);
      setError(null);
      setErrorReference(null);
    },
    onError: (err) => {
      setError(isGivmoError(err) ? err.message : 'Something went wrong. Please try again.');
      setErrorReference(isGivmoError(err) ? supportReference(err) : null);
    },
  });
  const submitting = updateMutation.isPending;

  const startEdit = () => {
    setForm(saved);
    setError(null);
    setErrorReference(null);
    setEditing(true);
  };
  const cancelEdit = () => {
    setForm(saved);
    setError(null);
    setErrorReference(null);
    setEditing(false);
  };
  const save = () => {
    if (!dirty) return;
    setError(null);
    setErrorReference(null);
    updateMutation.mutate({ body: buildAccountUpdate(company, form) });
  };

  return (
    <div className="flex flex-col gap-16 pt-16" {...testIdProp(`${PREFIX}-tab`)}>
      <Card>
        <CardBody className="flex flex-col gap-20">
          <div className="flex items-center justify-between gap-16">
            <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              Account information
            </h2>
            {!editing && (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                leadingIcon={<Icon icon={Pencil} size={14} aria-hidden />}
                onClick={startEdit}
                testId={`${PREFIX}-edit`}
              >
                Edit
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-start gap-20">
            {/* Company logo (read-only display + upload). */}
            <CompanyLogo company={company} />

            <div className="w-[320px] max-w-full">
              <Field
                label="Company name"
                value={form.name}
                readOnly={!editing || submitting}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                testId="account-info-name"
              />
            </div>

            <div className="w-[320px] max-w-full">
              <LocationField
                value={form.location}
                editing={editing && !submitting}
                onChange={(location) => setForm((f) => ({ ...f, location }))}
              />
            </div>

            <div className="w-[180px] max-w-full">
              <Field
                label="# of Employees"
                type="number"
                min={0}
                inputMode="numeric"
                value={form.employees}
                readOnly={!editing || submitting}
                onChange={(e) => setForm((f) => ({ ...f, employees: e.target.value }))}
                testId="account-info-employees"
              />
            </div>
          </div>

          {error !== null && (
            <div className="flex flex-col gap-4">
              <p
                className="font-sans text-s14 font-w500 tracking-normal text-danger-700"
                {...testIdProp(`${PREFIX}-error`)}
              >
                {error}
              </p>
              {errorReference !== null && (
                <p
                  className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary"
                  {...testIdProp(`${PREFIX}-error-reference`)}
                >
                  {errorReference}
                </p>
              )}
            </div>
          )}
        </CardBody>

        {editing && (
          <CardFooter className="justify-end gap-8">
            <Button
              appearance="saas"
              tone="ghost"
              size="md"
              disabled={submitting}
              onClick={cancelEdit}
              testId={`${PREFIX}-cancel`}
            >
              Cancel
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              loading={submitting}
              disabled={!dirty || submitting}
              onClick={save}
              testId={`${PREFIX}-save`}
            >
              Save changes
            </Button>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}

// ── Location autocomplete — debounced search → predictions, ", USA" stripped on pick ───────────────
function LocationField({
  value,
  editing,
  onChange,
}: {
  value: string;
  editing: boolean;
  onChange: (location: string) => void;
}) {
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  // Debounce the search input ~300ms.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(input.trim()), LOCATION_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input]);

  const search = useQuery({
    ...companiesGetUserLocationOptions({ query: { input: debounced } }),
    enabled: editing && debounced.length > 0,
  });
  const predictions = useMemo<LocationPredictionOut[]>(
    () => (Array.isArray(search.data) ? search.data : []),
    [search.data],
  );

  if (!editing) {
    // Read-only display of the persisted location.
    return <Field label="Location" value={value} readOnly testId="account-info-location" />;
  }

  return (
    <div className="relative flex flex-col">
      <Field
        label="Location"
        placeholder="Search for the location..."
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        testId="account-info-location"
      />
      {value.length > 0 && (
        <p className="mt-4 font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
          Selected: {value}
        </p>
      )}
      {open && debounced.length > 0 && predictions.length > 0 && (
        <ul
          className="absolute top-full z-10 mt-2 max-h-[220px] w-full overflow-y-auto rounded-lg border border-border-subtle bg-surface p-4 shadow-md"
          {...testIdProp('account-info-location-predictions')}
        >
          {predictions.map((p) => {
            const stripped = stripUsaSuffix(p.description);
            return (
              <li key={p.place_id}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(stripped);
                    setInput('');
                    setOpen(false);
                  }}
                  className="w-full rounded-md px-12 py-8 text-left font-sans text-s14 tracking-normal text-text outline-none transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover"
                  {...testIdProp('account-info-location-prediction')}
                >
                  {stripped}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── Company logo: read-only display + multipart upload (the company's logo IS its profile pic) ─────
function CompanyLogo({ company }: { company: CompanyDetailedOut }) {
  const queryClient = useQueryClient();
  const [cacheBuster, setCacheBuster] = useState(() => Date.now());

  const uploadLogo = useMutation({
    ...companiesUploadCompanyProfilePictureMutation(),
    onSuccess: async () => {
      setCacheBuster(Date.now());
      await queryClient.invalidateQueries({ queryKey: companiesGetCompanyQueryKey() });
      toast.success('Logo updated');
    },
    onError: (err) =>
      toast.error(isGivmoError(err) ? err.message : 'Could not update the logo. Please retry.'),
  });

  const onPick = (file: File) => {
    // Fail fast on a wrong format/size with a clear message; the backend re-validates authoritatively.
    const problem = validateLogoFile(file);
    if (problem !== null) {
      toast.error(problem);
      return;
    }
    uploadLogo.mutate({ body: { file } });
  };

  const logoUrl = company.logo_url;
  const logoSrc =
    typeof logoUrl === 'string' && logoUrl.length > 0
      ? `${logoUrl}${logoUrl.includes('?') ? '&' : '?'}v=${cacheBuster}`
      : null;

  return (
    <div {...testIdProp('account-info-logo')}>
      <UploadDropzone
        label="Company logo"
        ariaLabel="Upload company logo"
        accept={ACCEPTED_LOGO_TYPES}
        src={logoSrc}
        imageAlt={`${company.name} logo`}
        hint="Add logo"
        disabled={uploadLogo.isPending}
        onFile={onPick}
        testId="account-info-logo-button"
        inputTestId="account-info-logo-input"
        imageTestId="account-info-logo-image"
      />
    </div>
  );
}
